// A tiny pixel font (5 rows tall, capitals only, most letters 3 wide), for speech bubbles and the
// gallery's labels. Text drawn by the browser looks slightly different on every system, which
// would change every picture when the gallery is made on another computer; these letters are
// plain squares and always come out the same, and they suit the pixel companions.
//
// It writes English and the languages that use Latin or Cyrillic capitals: an accented letter is
// its plain letter with its mark drawn above it (or below: Ç), so accents reach up to three rows
// over the capitals and a cedilla one row under them (`reach`, for a layout that leaves room).

const GLYPHS = {
  A: '.#./#.#/###/#.#/#.#', B: '##./#.#/##./#.#/##.', C: '.##/#../#../#../.##', D: '##./#.#/#.#/#.#/##.',
  E: '###/#../##./#../###', F: '###/#../##./#../#..', G: '.##/#../#.#/#.#/.##', H: '#.#/#.#/###/#.#/#.#',
  I: '###/.#./.#./.#./###', J: '..#/..#/..#/#.#/.#.', K: '#.#/#.#/##./#.#/#.#', L: '#../#../#../#../###',
  M: '#...#/##.##/#.#.#/#...#/#...#', N: '#..#/##.#/#.##/#..#/#..#', O: '.#./#.#/#.#/#.#/.#.', P: '##./#.#/##./#../#..',
  Q: '.#./#.#/#.#/##./.##', R: '##./#.#/##./#.#/#.#', S: '.##/#../.#./..#/##.', T: '###/.#./.#./.#./.#.',
  U: '#.#/#.#/#.#/#.#/###', V: '#...#/#...#/.#.#./.#.#./..#..', W: '#...#/#...#/#.#.#/##.##/#...#', X: '#.#/#.#/.#./#.#/#.#',
  Y: '#.#/#.#/.#./.#./.#.', Z: '###/..#/.#./#../###',
  0: '###/#.#/#.#/#.#/###', 1: '.#./##./.#./.#./###', 2: '##./..#/.#./#../###', 3: '##./..#/.#./..#/##.',
  4: '#.#/#.#/###/..#/..#', 5: '###/#../##./..#/##.', 6: '.##/#../###/#.#/###', 7: '###/..#/..#/.#./.#.',
  8: '###/#.#/###/#.#/###', 9: '###/#.#/###/..#/##.',
  '.': '.../.../.../.../.#.', ',': '.../.../.../.#./#..', ':': '.../.#./.../.#./...', ';': '.../.#./.../.#./#..', '-': '.../.../###/.../...',
  '+': '.../.#./###/.#./...', '/': '..#/..#/.#./#../#..', '(': '.#./#../#../#../.#.', ')': '.#./..#/..#/..#/.#.',
  "'": '.#./.#./.../.../...', '?': '##./..#/.#./.../.#.', '!': '.#./.#./.#./.../.#.', '=': '.../###/.../###/...',
  '&': '.#./#.#/.#./#.#/.##', '"': '#.#/#.#/.../.../...', '%': '#.#/..#/.#./#../#.#', '_': '.../.../.../.../###', ' ': '.../.../.../.../...',
  '^': '.#./#.#/.../.../...',
  // Other languages' punctuation and letters.
  ' ': '.../.../.../.../...', ' ': '././././.',   // no-break spaces (French « Oui ! »): the bubbles don't break lines there
  '¡': '.#./.../.#./.#./.#.', '¿': '.#./.../.#./#../.##', '…': '...../...../...../...../#.#.#',
  '–': '..../..../####/..../....', '—': '...../...../#####/...../.....', '·': '.../.../.#./.../...',
  '‘': '#./.#/../../..', '’': '.#/#./../../..', '‚': '../../../.#/#.',
  '“': '#.#./.#.#/..../..../....', '”': '.#.#/#.#./..../..../....', '„': '..../..../..../.#.#/#.#.',
  '«': '..../.#.#/#.#./.#.#/....', '»': '..../#.#./.#.#/#.#./....', '‹': '../.#/#./.#/..', '›': '../#./.#/#./..',
  'Œ': '.####/#.#../#.##./#.#../.####', 'Æ': '.####/#.#../####./#.#../#.###',
  // Cyrillic capitals (those that look like Latin ones are the same letters).
  'Б': '###/#../##./#.#/##.', 'Г': '###/#../#../#../#..', 'Д': '.###/.#.#/.#.#/####/#..#', 'Ж': '#.#.#/#.#.#/.###./#.#.#/#.#.#',
  'З': '##./..#/.#./..#/##.', 'И': '#..#/#..#/#.##/##.#/#..#', 'Л': '..##/.#.#/.#.#/.#.#/#..#', 'П': '###/#.#/#.#/#.#/#.#',
  'У': '#.#/#.#/.##/..#/##.', 'Ф': '.###./#.#.#/#.#.#/.###./..#..', 'Ц': '#.#./#.#./#.#./####/...#', 'Ч': '#.#/#.#/.##/..#/..#',
  'Ш': '#.#.#/#.#.#/#.#.#/#.#.#/#####', 'Щ': '#.#.#./#.#.#./#.#.#./######/.....#', 'Ъ': '##../.#../.##./.#.#/.##.',
  'Ы': '#...#/#...#/##..#/#.#.#/##..#', 'Ь': '#../#../##./#.#/##.', 'Э': '##./..#/.##/..#/##.', 'Ю': '#..#./#.#.#/###.#/#.#.#/#..#.',
  'Я': '.##/#.#/.##/#.#/#.#',
};
for (const [cyrillic, latin] of Object.entries({ 'А': 'A', 'В': 'B', 'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O', 'Р': 'P', 'С': 'C', 'Т': 'T', 'Х': 'X' })) GLYPHS[cyrillic] = GLYPHS[latin];

// The marks an accented letter is made of (its Unicode decomposition, NFD: É is E and U+0301),
// rows from the top, centred over the letter; '' is an empty row between mark and letter.
const MARKS = {
  '̀': { up: ['#..', '.#.', ''] },        // grave: À È Ì Ò Ù
  '́': { up: ['..#', '.#.', ''] },        // acute: Á É Í Ó Ú
  '̂': { up: ['.#.', '#.#', ''] },        // circumflex: Â Ê Î Ô Û
  '̃': { up: ['.#.#', '#.#.', ''] },      // tilde: Ã Õ Ñ
  '̈': { up: ['#.#', ''] },               // diaeresis: Ä Ö Ü Ë Ï Ÿ Ё
  '̆': { up: ['#..#', '.##.', ''] },      // breve: Й
  '̧': { down: ['.#.'] },                 // cedilla: Ç
};

// A letter's drawing: { rows: its 5 rows, up: rows over them, down: rows under them, width },
// or null when the font has no such letter.
const cache = new Map();
function letter(ch) {
  if (cache.has(ch)) return cache.get(ch);
  let found = null;
  if (GLYPHS[ch]) {
    const rows = GLYPHS[ch].split('/');
    found = { rows, up: [], down: [], width: rows[0].length };
  } else {
    const [base, ...marks] = [...ch.normalize('NFD')];
    const plain = marks.length && GLYPHS[base] && marks.every(m => MARKS[m]) ? letter(base) : null;
    if (plain) {
      const place = rows => rows.map(r => {
        if (!r) return ''.padEnd(plain.width, '.');
        const off = Math.floor((plain.width - r.length) / 2);
        return [...Array(plain.width)].map((_, c) => r[c - off] ?? '.').join('');
      });
      found = { rows: plain.rows, up: [], down: [], width: plain.width };
      for (const m of marks) {
        if (MARKS[m].up) found.up = [...place(MARKS[m].up), ...found.up];
        if (MARKS[m].down) found.down = [...found.down, ...place(MARKS[m].down)];
      }
    }
  }
  cache.set(ch, found);
  return found;
}
const UNKNOWN = letter('?');
const drawing = ch => letter(ch) || UNKNOWN;

// A letter's rows, '#' where it's drawn; unknown letters show as '?'.
export const glyph = ch => drawing(ch).rows;

// Whether the font has every letter of str (after making it capitals).
export const canWrite = str => [...String(str).toUpperCase()].every(ch => /\s/.test(ch) || !!letter(ch));

// How many rows str's letters reach over and under the capitals ({ up, down }: accents, a cedilla).
export function reach(str) {
  let up = 0, down = 0;
  for (const ch of String(str).toUpperCase()) { const l = drawing(ch); up = Math.max(up, l.up.length); down = Math.max(down, l.down.length); }
  return { up, down };
}

// Each letter is followed by one empty column.
export const textWidth = (str, scale = 1) => Math.max(0, [...str.toUpperCase()].reduce((w, ch) => w + drawing(ch).width + 1, 0) - 1) * scale;

// Draws str with its top-left at (x, y) (the capitals' top: accents go above it); each font pixel
// is scale x scale screen pixels.
export function drawText(ctx, str, x, y, scale, color) {
  ctx.fillStyle = color;
  let cx = Math.round(x);
  for (const ch of str.toUpperCase()) {
    const l = drawing(ch), top = Math.round(y) - l.up.length * scale;
    [...l.up, ...l.rows, ...l.down].forEach((row, r) => [...row].forEach((bit, c) => {
      if (bit === '#') ctx.fillRect(cx + c * scale, top + r * scale, scale, scale);
    }));
    cx += (l.width + 1) * scale;
  }
  return cx - x;
}

// Splits text into lines that fit maxW pixels.
export function wrap(str, scale, maxW) {
  const lines = [];
  let line = '';
  for (const word of str.split(/\s+/)) {
    const next = line ? line + ' ' + word : word;
    if (line && textWidth(next, scale) > maxW) { lines.push(line); line = word; } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}
